import AuthService from "utilities/services/auth/auth-service";
import EmulationTokenRecord from "models/view-models/emulation-token-record";
import useCurrentIdentity from "utilities/hooks/use-current-identity";
import useLocalStorage from "utilities/hooks/use-local-storage";
import { RouteUtils } from "utilities/route-utils";
import { siteMap } from "internal-sitemap";
import { useAuthentication } from "utilities/contexts/authentication/authentication-provider";
import React, {
    PropsWithChildren,
    createContext,
    useCallback,
    useMemo,
    useState,
} from "react";
import { useGlobalState } from "../use-global-state-context";
import FullScreenTransition from "organisms/full-screen-transition/full-screen-transition";
import { useHistory } from "react-router-dom";
import UserConfigurationErrorKeys from "constants/user-configuration-error-keys";
import useUnauthorizedResult from "utilities/hooks/use-unauthorized-result";
import { atom, useAtom } from "jotai";
import LocalStorageKey from "utilities/enumerations/local-storage-keys";
import { LocalStorageUtils } from "utilities/local-storage-utils";

export const useShowEndingEmulationLoader = atom<boolean>();

interface EmulationModeContextState {
    token: EmulationTokenRecord | null;
    setToken: (value: EmulationTokenRecord | null) => void;
    userEmail: string;
    loadingEmulationSession: boolean;
    setLoadingEmulationSession: React.Dispatch<React.SetStateAction<boolean>>;
    endingEmulationSession: boolean;
    setEndingEmulationSession: React.Dispatch<React.SetStateAction<boolean>>;
    endEmulationMode: (guestId: number | undefined) => Promise<void>;
}

const defaultState: EmulationModeContextState = {
    token: null,
    setToken: () => {},
    userEmail: "",
    loadingEmulationSession: false,
    setLoadingEmulationSession: () => {},
    endingEmulationSession: false,
    setEndingEmulationSession: () => {},
    endEmulationMode: async () => {},
};

export const EmulationModeContext = createContext(defaultState);

const EmulationModeContextProvider = (props: PropsWithChildren<{}>) => {
    const [showEndingEmulationLoader, setShowEndingEmulationLoader] = useAtom(
        useShowEndingEmulationLoader
    );

    const { storedValue: token, setValue: setToken } =
        useLocalStorage<EmulationTokenRecord | null>(
            LocalStorageKey.EmulationToken,
            null
        );

    const {
        endEmulationSession,
        verifyLoginAfterEndEmulation,
        deleteEmulatedUserLoginFromApi,
        authenticationLifeCycleStatus,
    } = useAuthentication();
    const { build } = useCurrentIdentity();

    const [loadingEmulationSession, setLoadingEmulationSession] =
        useState(false);
    const [endingEmulationSession, setEndingEmulationSession] = useState(false);

    const { globalState } = useGlobalState();
    const userEmail = globalState.currentIdentity?.user?.email ?? "";

    const history = useHistory();

    const emulationTransitionText = useMemo(() => {
        return loadingEmulationSession
            ? "Loading emulation session"
            : endingEmulationSession
              ? "Ending emulation session"
              : "";
    }, [endingEmulationSession, loadingEmulationSession]);

    useUnauthorizedResult(
        UserConfigurationErrorKeys.ERROR_NO_SUBSCRIPTIONS,
        UserConfigurationErrorKeys.ERROR_CURRENT_ROLE_EXPIRED,
        UserConfigurationErrorKeys.ERROR_EULA_NOT_ACCEPTED,
        UserConfigurationErrorKeys.ERROR_HAS_HAD_FREE_TRIAL
    );

    const endEmulationMode = useCallback(
        async (guestId: number | undefined) => {
            if (endingEmulationSession) {
                return;
            }
            setEndingEmulationSession(true);
            setShowEndingEmulationLoader(true);

            //This call deleted the EmulationToken cookie and LocalStorage token
            const endSuccess = await endEmulationSession();
            if (endSuccess) {
                //In the ABSENCE of an EmulationToken,
                //this will log the admin back into the API
                //It issues an new CODSession cookie.
                const userLogin = await AuthService.create();

                await verifyLoginAfterEndEmulation();
                deleteEmulatedUserLoginFromApi();
                //this is useless.  The call is much further up the stack
                setEndingEmulationSession(false);
                setShowEndingEmulationLoader(false);
                LocalStorageUtils.remove(
                    LocalStorageKey.EmulationOriginalUserLogin
                );

                history.push(
                    RouteUtils.getUrl(siteMap.admin.support.userDetail, {
                        id: guestId,
                    })
                );
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [build, history, endEmulationSession, setEndingEmulationSession]
    );

    const value: EmulationModeContextState = {
        token,
        setToken,
        userEmail,
        loadingEmulationSession,
        setLoadingEmulationSession,
        endingEmulationSession,
        setEndingEmulationSession,
        endEmulationMode,
    };

    return (
        <EmulationModeContext.Provider value={value}>
            {loadingEmulationSession || endingEmulationSession ? (
                <FullScreenTransition
                    transitionText={emulationTransitionText}
                />
            ) : (
                props.children
            )}
        </EmulationModeContext.Provider>
    );
};

export default EmulationModeContextProvider;
